import { AlertTriangle, CheckCircle, Lightbulb } from "lucide-react";

// Section types
export type ServiceSection =
  | { type: "prose"; heading: string; paragraphs: string[] }
  | { type: "process"; heading: string; steps: { title: string; desc: string }[] }
  | { type: "warnings"; heading: string; items: string[] }
  | { type: "stats"; items: { value: string; label: string }[] }
  | {
      type: "comparison";
      heading: string;
      optionA: { title: string; points: string[] };
      optionB: { title: string; points: string[] };
    }
  | { type: "tip"; heading: string; text: string };

const ProseSection = ({ heading, paragraphs }: { heading: string; paragraphs: string[] }) => (
  <section className="py-12 md:py-16">
    <div className="container max-w-3xl">
      <h2 className="text-3xl font-bold mb-6">{heading}</h2>
      <div className="space-y-4">
        {paragraphs.map((p, i) => (
          <p key={i} className="text-foreground/80 leading-relaxed text-lg">
            {p}
          </p>
        ))}
      </div>
    </div>
  </section>
);

const ProcessSection = ({
  heading,
  steps,
}: {
  heading: string;
  steps: { title: string; desc: string }[];
}) => (
  <section className="py-12 md:py-16 bg-muted/30">
    <div className="container">
      <h2 className="text-3xl font-bold text-center mb-10">{heading}</h2>
      <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
        {steps.map((step, i) => (
          <div
            key={i}
            className="relative bg-card border border-border rounded-xl p-6 text-center"
          >
            <div className="w-10 h-10 bg-primary text-primary-foreground rounded-full flex items-center justify-center font-bold text-lg mx-auto mb-4">
              {i + 1}
            </div>
            <h3 className="font-bold text-lg mb-2">{step.title}</h3>
            <p className="text-muted-foreground text-sm">{step.desc}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const WarningsSection = ({ heading, items }: { heading: string; items: string[] }) => (
  <section className="py-12 md:py-16">
    <div className="container">
      <h2 className="text-3xl font-bold text-center mb-8">{heading}</h2>
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 max-w-4xl mx-auto">
        {items.map((item, i) => (
          <div
            key={i}
            className="flex items-start gap-3 bg-destructive/5 border border-destructive/20 rounded-lg p-4"
          >
            <AlertTriangle className="h-5 w-5 text-destructive flex-shrink-0 mt-0.5" />
            <p className="text-foreground/80 text-sm">{item}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const StatsSection = ({ items }: { items: { value: string; label: string }[] }) => (
  <section className="py-12 md:py-16 bg-primary/5">
    <div className="container">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
        {items.map((stat, i) => (
          <div key={i} className="text-center">
            <div className="text-3xl md:text-4xl font-bold text-primary mb-1">
              {stat.value}
            </div>
            <p className="text-muted-foreground text-sm">{stat.label}</p>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const ComparisonSection = ({
  heading,
  optionA,
  optionB,
}: {
  heading: string;
  optionA: { title: string; points: string[] };
  optionB: { title: string; points: string[] };
}) => (
  <section className="py-12 md:py-16 bg-muted/30">
    <div className="container">
      <h2 className="text-3xl font-bold text-center mb-8">{heading}</h2>
      <div className="grid md:grid-cols-2 gap-6 max-w-3xl mx-auto">
        {[optionA, optionB].map((option, i) => (
          <div key={i} className="bg-card border border-border rounded-xl p-6">
            <h3 className="font-bold text-xl mb-4 text-center">{option.title}</h3>
            <ul className="space-y-3">
              {option.points.map((point, j) => (
                <li key={j} className="flex items-start gap-2">
                  <CheckCircle className="h-4 w-4 text-primary flex-shrink-0 mt-1" />
                  <span className="text-foreground/80 text-sm">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </div>
    </div>
  </section>
);

const TipSection = ({ heading, text }: { heading: string; text: string }) => (
  <section className="py-12 md:py-16">
    <div className="container max-w-3xl">
      <div className="bg-accent/10 border border-accent/30 rounded-xl p-6 md:p-8">
        <div className="flex items-start gap-4">
          <Lightbulb className="h-6 w-6 text-accent flex-shrink-0 mt-1" />
          <div>
            <h3 className="font-bold text-lg mb-2">{heading}</h3>
            <p className="text-foreground/80 leading-relaxed">{text}</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

export const renderSection = (section: ServiceSection, index: number) => {
  switch (section.type) {
    case "prose":
      return <ProseSection key={index} heading={section.heading} paragraphs={section.paragraphs} />;
    case "process":
      return <ProcessSection key={index} heading={section.heading} steps={section.steps} />;
    case "warnings":
      return <WarningsSection key={index} heading={section.heading} items={section.items} />;
    case "stats":
      return <StatsSection key={index} items={section.items} />;
    case "comparison":
      return (
        <ComparisonSection
          key={index}
          heading={section.heading}
          optionA={section.optionA}
          optionB={section.optionB}
        />
      );
    case "tip":
      return <TipSection key={index} heading={section.heading} text={section.text} />;
    default:
      return null;
  }
};
